import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useClassrooms } from "../data/ClassroomContext";
import {
  ASSIGNMENT_STATUS,
  PROGRESS_STATE,
  progressCounts,
  purposeLabel,
} from "../data/assignments";
import { closeAssignment } from "../data/teacherWorkApi";
import { useAssignment, useClassroomLearners } from "../data/useClassroomWorkspace";
import { announceWorkspaceChange } from "../data/usePolledResource";
import ConfirmSheet from "../components/ConfirmSheet";
import { BackIcon } from "../components/Icons";
import WorkspaceStatus from "../components/WorkspaceStatus";
import { formatWhen } from "../components/WorkState";
import ui from "../components/ui.module.css";

// One assignment and each learner's progress, derived by the gateway from the
// assignment, its attempts and the class's active enrolments. Finished work is
// reviewed through the same review view as lesson work.
const AssignmentDetail = () => {
  const { assignmentId } = useParams();
  const { active } = useClassrooms();
  const detail = useAssignment(active.id, assignmentId);
  const learners = useClassroomLearners(active.id);
  const [closing, setClosing] = useState({ confirm: false, status: "idle" });

  const names = new Map((learners.data?.learners || []).map((l) => [l.id, l.name]));
  const assignment = detail.data?.assignment;
  const progress = [...(detail.data?.progress || [])].sort((a, b) =>
    (names.get(a.learnerId) || "").localeCompare(names.get(b.learnerId) || "")
  );
  const counts = progressCounts(progress);

  const close = async () => {
    setClosing({ confirm: false, status: "closing" });
    try {
      await closeAssignment(active.id, assignmentId);
      setClosing({ confirm: false, status: "idle" });
      announceWorkspaceChange();
    } catch {
      setClosing({ confirm: false, status: "failed" });
    }
  };

  return (
    <div className={ui.page}>
      <Link to="/work" className={ui.textButton}>
        <BackIcon />
        Work
      </Link>
      <div className={ui.stack}>
        <p className={ui.eyebrow}>
          {assignment ? [purposeLabel(assignment.purpose), active.name].join(" · ") : active.name}
        </p>
        <h1 className={ui.title}>{assignment?.title || "Homework and tests"}</h1>
        {assignment && (
          <p className={ui.meta}>
            {[
              ASSIGNMENT_STATUS[assignment.status],
              assignment.opensAt && `Starts ${formatWhen(assignment.opensAt)}`,
              assignment.dueAt && `Due ${formatWhen(assignment.dueAt)}`,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        )}
      </div>

      <WorkspaceStatus resource={detail} loadingLabel="Loading progress…" className={active.name} />

      {assignment && (
        <>
          <p className={ui.notice} role="status">
            {counts.completed} finished · {counts.working} working · {counts.not_started} not started
          </p>
          <ul className={ui.list}>
            {progress.map((row) => {
              const name = names.get(row.learnerId) || "Learner";
              return (
                <li key={row.learnerId} className={`${ui.card} ${ui.workItem}`}>
                  <div className={ui.stack} style={{ gap: "0.25rem" }}>
                    <p className={ui.cardTitle}>{name}</p>
                    <p className={ui.meta}>
                      {[PROGRESS_STATE[row.state], row.completedAt && formatWhen(row.completedAt)]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  {row.attemptId && (
                    <Link
                      to={`/work/${row.attemptId}`}
                      state={{ from: `/work/assignments/${assignmentId}` }}
                      className={`${ui.button} ${row.state === "completed" ? ui.primary : ui.secondary}`}
                      aria-label={`${row.state === "completed" ? "Review" : "See"} ${name}'s work`}
                    >
                      {row.state === "completed" ? "Review" : "See work"}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
          {!progress.length && <p className={ui.meta}>No learners are in this class right now.</p>}

          {assignment.status === "assigned" && (
            <div className={ui.stack}>
              {closing.status === "failed" && (
                <p className={ui.error} role="alert">
                  It was not closed. Check your connection and try again.
                </p>
              )}
              <button
                type="button"
                className={`${ui.button} ${ui.secondary}`}
                onClick={() => setClosing({ confirm: true, status: "idle" })}
                disabled={closing.status === "closing"}
              >
                {closing.status === "closing" ? "Closing…" : "Close"}
              </button>
            </div>
          )}
        </>
      )}
      {closing.confirm && (
        <ConfirmSheet
          title={`Close this ${purposeLabel(assignment?.purpose).toLowerCase()}?`}
          confirmLabel="Close"
          cancelLabel="Keep open"
          onConfirm={close}
          onCancel={() => setClosing({ confirm: false, status: "idle" })}
        >
          <p>Learners who have not started can no longer start. Learners who started keep their work and can finish.</p>
        </ConfirmSheet>
      )}
    </div>
  );
};

export default AssignmentDetail;
